import { User, Route } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import type { Schedule, WatchlistScope } from "../../watchlist/types";
import { SCHEDULE_PRESETS, WEEKLY_DAYS, WINDOW_PRESETS } from "../../watchlist/types";
import type { FilterOption } from "../FilterMultiSelect";
import FilterMultiSelect from "../FilterMultiSelect";
import DaysSelect from "./DaysSelect";

type WatchScopeControlsProps = {
  scope: WatchlistScope;
  onScopeChange: (scope: WatchlistScope) => void;
  schedule?: Schedule;
  onScheduleChange?: (schedule: Schedule) => void;
  userOptions: FilterOption[];
  processOptions: FilterOption[];
};

/**
 * The canonical editor for a Watch's people, processes, time window, and
 * optional cadence. Both the composer and final review use this component so
 * the same scope always has the same controls and selection semantics.
 */
const WatchScopeControls = ({
  scope,
  onScopeChange,
  schedule,
  onScheduleChange,
  userOptions,
  processOptions,
}: WatchScopeControlsProps) => {
  const { t } = useTranslation();
  const showSchedule = schedule !== undefined && onScheduleChange !== undefined;

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
      <FilterMultiSelect
        pill
        icon={<User size={12} className="shrink-0 text-text-dim" />}
        allLabel={t("watchlist.all_people")}
        searchPlaceholder={t("search_people")}
        multipleLabel={count => t("dashboard.people_selected", { count })}
        selectAllLabel={t("watchlist.all_people")}
        emptyLabel={t("watchlist.select_people")}
        value={scope.userIds}
        options={userOptions}
        onChange={userIds => onScopeChange({ ...scope, userIds })}
      />

      <FilterMultiSelect
        pill
        icon={<Route size={12} className="shrink-0 text-text-dim" />}
        value={
          scope.processSlugs.length === 0 && scope.allProcesses !== false
            ? processOptions.map(option => option.id)
            : scope.processSlugs
        }
        options={processOptions}
        onChange={processSlugs => onScopeChange({ ...scope, processSlugs, allProcesses: false })}
        allLabel={t("watchlist.all_processes")}
        emptyLabel={t("watchlist.select_processes")}
        selectAllLabel={t("watchlist.all_processes")}
        searchPlaceholder={t("search_in_processes")}
      />

      <DaysSelect
        value={scope.window.days}
        onChange={days => onScopeChange({ ...scope, window: { days } })}
        presets={WINDOW_PRESETS}
        format={days => t("watchlist.window_last_n_days", { count: days })}
        unitLabel={t("watchlist.days_back")}
      />

      {showSchedule && (
        <DaysSelect
          value={schedule.everyDays}
          onChange={everyDays => onScheduleChange({ everyDays })}
          presets={SCHEDULE_PRESETS}
          format={days =>
            days === WEEKLY_DAYS
              ? t("watchlist.runs_weekly")
              : t("watchlist.runs_every_n_days_count", { count: days })
          }
          unitLabel={t("watchlist.days_between_runs")}
        />
      )}
    </div>
  );
};

export default WatchScopeControls;
